import type { TermSummary } from "../../rdf/types";
import type { HierarchyNode } from "./hierarchy";
import { KIND_STYLE } from "../../components/kind-style";

interface TreeNavProps {
  nodes: HierarchyNode[];
  selectedIri: string | null;
  expandedIris: ReadonlySet<string>;
  onToggleExpand: (iri: string) => void;
  onSelect: (term: TermSummary) => void;
}

export default function TreeNav({
  nodes,
  selectedIri,
  expandedIris,
  onToggleExpand,
  onSelect,
}: TreeNavProps) {
  if (nodes.length === 0) {
    return (
      <p className="px-4 py-4 text-sm text-[var(--oe-text-muted)]">
        No terms in the active datasets.
      </p>
    );
  }

  return (
    <ul role="tree" className="py-1.5">
      {nodes.map((node) => (
        <TreeItem
          key={node.term.iri}
          node={node}
          depth={0}
          selectedIri={selectedIri}
          expandedIris={expandedIris}
          onToggleExpand={onToggleExpand}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}

function TreeItem({
  node,
  depth,
  selectedIri,
  expandedIris,
  onToggleExpand,
  onSelect,
}: {
  node: HierarchyNode;
  depth: number;
} & Omit<TreeNavProps, "nodes">) {
  const { term, children } = node;
  const hasChildren = children.length > 0;
  const isExpanded = expandedIris.has(term.iri);
  const isSelected = selectedIri === term.iri;
  const style = KIND_STYLE[term.kind];

  return (
    <li role="treeitem" aria-expanded={hasChildren ? isExpanded : undefined}>
      <div
        className={`group mx-1.5 flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 text-[15px] leading-snug transition-colors ${
          isSelected
            ? "bg-[var(--oe-accent-soft)] font-medium text-[var(--oe-accent)]"
            : "text-[var(--oe-text)] hover:bg-[var(--oe-hover)]"
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => onSelect(term)}
      >
        <button
          type="button"
          aria-label={isExpanded ? "Collapse" : "Expand"}
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--oe-text-muted)] transition-colors hover:bg-[var(--oe-hover)] hover:text-[var(--oe-text)] ${
            hasChildren ? "" : "invisible"
          }`}
          onClick={(event) => {
            event.stopPropagation();
            if (hasChildren) onToggleExpand(term.iri);
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${isExpanded ? "rotate-90" : ""}`}
            aria-hidden="true"
          >
            <path d="m9 6 6 6-6 6" />
          </svg>
        </button>
        <span
          aria-hidden="true"
          className="w-4 shrink-0 text-center text-xs font-semibold"
          style={{ color: style.color }}
          title={style.label}
        >
          {style.glyph}
        </span>
        <span className="truncate">{term.label ?? term.prefixedName}</span>
        {hasChildren && !isExpanded && (
          <span className="ml-auto shrink-0 pl-1 text-xs text-[var(--oe-text-muted)] opacity-0 transition-opacity group-hover:opacity-100">
            {children.length}
          </span>
        )}
      </div>
      {hasChildren && isExpanded && (
        <ul role="group">
          {children.map((child) => (
            <TreeItem
              key={child.term.iri}
              node={child}
              depth={depth + 1}
              selectedIri={selectedIri}
              expandedIris={expandedIris}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
